import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
  computed,
  effect,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LoginService } from '../login.service';
import { PatientsService } from '../patients.service';
import {
  BirthdayApi,
  DashboardService,
  InvoiceApi,
  SubscriptionPaymentApi,
  UpcomingBirthApi,
} from '../dashboard.service';
import {
  Birthday,
  Invoice,
  KpiCard,
  MONTH_ABBR,
  UpcomingBirth,
  UpcomingBirthRaw,
} from '../interfaces/dashboard.component';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { LanguageService } from '../language.service';
import { CompactType, DirTypes, DisplayGrid, GridType, GridsterConfig } from 'angular-gridster2';
import {
  DASHBOARD_GRID_COLS,
  DASHBOARD_MARGIN,
  DASHBOARD_MOBILE_BREAKPOINT,
  DASHBOARD_ROW_HEIGHT,
  DASHBOARD_WIDGET_REGISTRY,
  DashboardGridItem,
  DashboardWidgetId,
  SavedLayoutItem,
  defaultDashboardLayout,
  mergeDashboardLayout,
  serializeDashboardLayout,
} from '../interfaces/dashboard-layout';

@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Dashboard implements OnInit {
  private readonly fromLogin: boolean;

  constructor(
    private loginService: LoginService,
    private dashboardService: DashboardService,
    private router: Router,
    private patientsService: PatientsService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {
    this.dir.set(this.languageService.pageDirection());
    this.applyDirToGrid();
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((e) => {
      this.dir.set(this.languageService.pageDirection());
      this.lang.set(e.lang);
      this.applyDirToGrid();
    });
    this.loginService.loginMissing();

    // Rebuild only when the visible set changes, so a late permission doesn't discard moved items.
    effect(() => {
      const ids = this.visibleWidgetIds();
      untracked(() =>
        this.layout.set(
          mergeDashboardLayout(this.committedLayout.length ? this.committedLayout : null, ids)
        )
      );
    });

    const parts = (loginService.username ?? '')
      .trim()
      .split(/\s+/)
      .filter((p) => p && p.toLowerCase() !== 'null');
    this.userNameLast = parts.pop() ?? '';
    this.userName = parts.join(' ');
    this.separatedMode = loginService.separatedClinic;
    this.fromLogin = this.router.currentNavigation()?.extras?.state?.['fromLogin'] === true;
    this.loadDashboard();
  }

  ngOnInit(): void {
    this.loginService.premissionsReady.subscribe((p) => {
      this.hasBillingPermission.set(!!p?.Billings);
      this.hasApptPermission.set(!!p?.Appointments);
      this.billingEnabled.set(!!p?.Billings);
      this.apptEnabled.set(!!p?.Appointments);
    });
    if (this.loginService.premissions) {
      this.hasBillingPermission.set(!!this.loginService.premissions.Billings);
      this.hasApptPermission.set(!!this.loginService.premissions.Appointments);
      this.billingEnabled.set(!!this.loginService.premissions.Billings);
      this.apptEnabled.set(!!this.loginService.premissions.Appointments);
    }
  }

  // ── User identity ──
  readonly userName: string;
  readonly userNameLast: string;

  // RTL positioning needs the `dir` on <section class="dash">.
  private applyDirToGrid(): void {
    this.gridOptions.dirType = this.dir() === 'rtl' ? DirTypes.RTL : DirTypes.LTR;
    this.gridOptions.api?.optionsChanged?.();
  }

  // ── Customize (edit mode) ──
  private applyEditModeToGrid(): void {
    const editing = this.editMode();
    this.gridOptions.draggable = { enabled: editing, stop: () => this.compactAfterDrop() };
    this.gridOptions.resizable = { enabled: editing, stop: () => this.compactAfterDrop() };
    this.gridOptions.displayGrid = editing ? DisplayGrid.OnDragAndResize : DisplayGrid.None;
    this.gridOptions.api?.optionsChanged?.();
  }

  private compactScheduled = false;

  // Compact once on drop, not continuously — left on, gravity blocks dragging top-row items down.
  private compactAfterDrop(): void {
    if (this.compactScheduled) {
      return;
    }
    this.compactScheduled = true;
    setTimeout(() => {
      this.compactScheduled = false;
      this.gridOptions.compactType = CompactType.CompactUp;
      this.gridOptions.api?.optionsChanged?.();
      this.gridOptions.compactType = CompactType.None;
      this.gridOptions.api?.optionsChanged?.();
      this.layoutRevision.update((revision) => revision + 1);
    });
  }

  startCustomize(): void {
    this.layoutSaveError.set(false);
    this.committedLayout = serializeDashboardLayout(this.layout());
    this.editMode.set(true);
    this.applyEditModeToGrid();
  }

  // Persist before leaving edit mode; a failed save stays in edit mode for retry.
  saveLayout(): void {
    if (this.savingLayout()) {
      return;
    }
    const snapshot = serializeDashboardLayout(this.layout());
    this.savingLayout.set(true);
    this.layoutSaveError.set(false);
    this.dashboardService.saveDashboardLayout(snapshot).subscribe({
      next: () => {
        this.savingLayout.set(false);
        this.committedLayout = snapshot;
        this.editMode.set(false);
        this.applyEditModeToGrid();
      },
      error: (err) => {
        console.error('Dashboard layout save failed', err);
        this.savingLayout.set(false);
        this.layoutSaveError.set(true);
      },
    });
  }

  cancelCustomize(): void {
    // Rebuild from the snapshot for a real undo (fresh items, not gridster's mutated ones).
    this.layout.set(mergeDashboardLayout(this.committedLayout, this.visibleWidgetIds()));
    this.editMode.set(false);
    this.applyEditModeToGrid();
  }

  // Stays in edit mode so Reset is undoable via Cancel and needs an explicit Save.
  resetLayout(): void {
    this.layout.set(defaultDashboardLayout(this.visibleWidgetIds()));
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const allowed = window.innerWidth >= DASHBOARD_MOBILE_BREAKPOINT;
    if (allowed === this.canCustomize()) {
      return;
    }
    this.canCustomize.set(allowed);
    if (!allowed && this.editMode()) {
      this.cancelCustomize();
    }
  }

  // ── Layout direction (RTL for Hebrew/Arabic interface languages) ──
  readonly dir = signal<'rtl' | 'ltr'>('ltr');
  // Bumped on every language change so instant()-based computeds re-translate.
  private readonly lang = signal('');

  // ── Header state ──
  private readonly hour = signal(new Date().getHours());
  readonly greeting = computed(() => {
    this.lang(); // re-translate when the interface language changes
    const h = this.hour();
    if (h < 12) return this.translate.instant('GUI.dashboard.labels.good morning');
    if (h < 18) return this.translate.instant('GUI.dashboard.labels.good afternoon');
    return this.translate.instant('GUI.dashboard.labels.good evening');
  });

  // Localised to the interface language; recomputed on change via the lang signal.
  readonly today = computed(() => {
    this.lang();
    const locale = this.translate.currentLang || 'en-US';
    return new Date().toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  });

  readonly separatedMode: boolean;
  readonly isAdmin: boolean = this.loginService.isAdmin;
  // Doctors and admins may raise "Expand your plan" feature requests.
  readonly canRequestFeatures: boolean =
    this.loginService.isAdmin || this.loginService.userType === 'doctor';

  readonly skipOnLogin = signal(false);
  toggleSkip(): void {
    const next = !this.skipOnLogin();
    this.skipOnLogin.set(next);
    this.dashboardService.updatePreferences(next).subscribe();
  }

  // ── Access toggles ──
  readonly hasBillingPermission = signal(false);
  readonly hasApptPermission = signal(false);
  readonly billingEnabled = signal(false);
  readonly apptEnabled = signal(false);

  toggleBilling(value: boolean): void {
    this.billingEnabled.set(value);
  }
  toggleAppt(value: boolean): void {
    this.apptEnabled.set(value);
  }

  // ── Loading state ──
  readonly loading = signal(true);
  readonly loadingInvoices = signal(true);
  readonly loadingStorage = signal(true);
  readonly loadingStorageSize = signal(true);
  readonly loadingBilling = signal(false);

  // ── Period signals ──
  readonly billingPeriod = signal<'today' | 'week' | 'month'>('week');
  readonly storagePeriod = signal<'today' | 'week' | 'month'>('week');

  readonly billingMonthLabel = computed(() => {
    this.lang();
    const p = this.billingPeriod();
    if (p === 'today') return 'Today';
    if (p === 'week') return 'Last 7 days';
    const locale = this.translate.currentLang || 'en-US';
    return new Date().toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  });

  private readonly billingData = signal({ total: 0, count: 0, isClinicWide: false });
  readonly billingIsClinicWide = signal(false);
  private readonly storageData = signal({ examCount: 0, imageCount: 0, videoCount: 0 });
  readonly storageSizeLabel = signal<string>('...');

  private readonly storageBytes = signal<number | null>(null);

  readonly storageValueLabel = computed(() => {
    this.lang();
    if (this.storageBytes() === 0) {
      return this.translate.instant('GUI.dashboard.labels.no storage used this month');
    }
    return this.storageSizeLabel();
  });

  onBillingPeriodChange(period: string): void {
    const p = period as 'today' | 'week' | 'month';
    this.billingPeriod.set(p);
    this.loadingBilling.set(true);
    this.dashboardService.getBillingStats(p).subscribe({
      next: (data) => {
        this.billingData.set(data);
        this.billingIsClinicWide.set(data.isClinicWide ?? false);
        this.loadingBilling.set(false);
      },
      error: () => this.loadingBilling.set(false),
    });
  }

  onStoragePeriodChange(period: string): void {
    const p = period as 'today' | 'week' | 'month';
    this.storagePeriod.set(p);
    this.loadingStorage.set(true);
    this.dashboardService.getStorageStats(p).subscribe({
      next: (data) => {
        this.storageData.set(data);
        this.loadingStorage.set(false);
      },
      error: () => this.loadingStorage.set(false),
    });
  }

  // ── KPI cards ──
  readonly kpiSubscription = computed<KpiCard>(() => {
    this.lang(); // re-translate instant() labels on language change
    return {
      icon: 'security',
      iconBg: 'var(--teal-bg)',
      iconColor: 'var(--teal-dark)',
      value: this.subPayments()[0]?.amount != null ? `₪${this.subPayments()[0]!.amount}` : '—',
      label: this.translate.instant('GUI.dashboard.labels.my subscription'),
      sub: this.translate.instant('GUI.dashboard.labels.last subscription payment'),
      loading: this.loading() || this.loadingSubPayment(),
      trend: this.translate.instant('GUI.dashboard.labels.chips.active'),
      trendTone: 'up' as const,
      id: 'kpi-subscription',
      scope: 'clinic' as const,
      updatedDaily: true,
      viewAllLink: '/dashboard/artisan-payments',
    };
  });

  readonly kpiPatientBilling = computed<KpiCard>(() => {
    this.lang();
    return {
      icon: 'credit_card',
      iconBg: 'var(--teal-bg)',
      iconColor: 'var(--teal-dark)',
      value: `₪${this.filteredInvoices()
        .reduce((sum, inv) => sum + inv.rawAmount, 0)
        .toLocaleString()}`,
      label:
        this.billingPeriod() === 'today'
          ? this.translate.instant('GUI.dashboard.labels.patient invoices today')
          : this.billingPeriod() === 'week'
            ? this.translate.instant('GUI.dashboard.labels.patient invoices this week')
            : this.translate.instant('GUI.dashboard.labels.patient invoices this month'),
      sub: `${this.filteredInvoices().length} ${this.translate.instant('GUI.dashboard.labels.invoices')}`,
      // KPI card skeleton: initial invoice load and each billing-period switch.
      loading: this.loading() || this.loadingInvoices() || this.loadingBilling(),
      trend: this.billingMonthLabel(),
      trendTone: 'flat' as const,
      id: 'kpi-patient-billing',
      periodSelect: 'billing' as const,
      // Invoices are read per clinic (invoices WHERE GID), so always clinic-wide.
      scope: 'clinic' as const,
    };
  });

  readonly kpiStorage = computed<KpiCard>(() => {
    this.lang();
    return {
      icon: 'storage',
      iconBg: 'var(--teal-bg)',
      iconColor: 'var(--teal-dark)',
      value: this.storageValueLabel(),
      valueLoading: this.loadingStorageSize(),
      smallValue: this.storageBytes() === 0,
      label: '',
      sub: `${this.storageData().examCount} ${this.translate.instant('GUI.dashboard.labels.chips.exams')} · ${this.storageData().imageCount} ${this.translate.instant('GUI.dashboard.labels.chips.images')} · ${this.storageData().videoCount} ${this.translate.instant('GUI.dashboard.labels.chips.videos')}`,
      loading: this.loading() || this.loadingStorage(),
      trend: this.translate.instant('GUI.dashboard.labels.chips.this month'),
      trendTone: 'flat' as const,
      id: 'kpi-storage',
      scope: 'clinic' as const,
      updatedDaily: true,
    };
  });

  // ── Grid layout ──
  readonly layout = signal<DashboardGridItem[]>([]);
  // Gates the grid so it mounts once already-arranged, never flashing the default.
  readonly layoutReady = signal(false);
  readonly editMode = signal(false);
  // Guards against a double-submit while the save PUT is inflight.
  readonly savingLayout = signal(false);
  readonly layoutSaveError = signal(false);
  // Below the mobile breakpoint gridster ignores x/cols, so a saved layout wouldn't match.
  readonly canCustomize = signal(window.innerWidth >= DASHBOARD_MOBILE_BREAKPOINT);

  // Serialized, not by reference — gridster mutates live items, breaking undo.
  private committedLayout: SavedLayoutItem[] = [];

  // Gridster mutates items in place so the signal never fires on drag; its callbacks bump this to drive gridHeight.
  private readonly layoutRevision = signal(0);

  // Compute the parent height ourselves (gridster's setGridSize sizes both axes and overflows width); null on mobile.
  readonly gridHeight = computed<number | null>(() => {
    this.layoutRevision();
    if (!this.canCustomize()) {
      return null;
    }
    const items = this.layout();
    const rows = items.reduce((bottom, item) => Math.max(bottom, item.y + item.rows), 0);
    const contentHeight = rows * DASHBOARD_ROW_HEIGHT + Math.max(rows - 1, 0) * DASHBOARD_MARGIN;
    if (!this.editMode()) {
      return contentHeight;
    }
    // Slack below so a tall widget clears boundaryControl's clamp when dragged down.
    const tallestRows = items.reduce((max, item) => Math.max(max, item.rows), 0);
    const slack = tallestRows * (DASHBOARD_ROW_HEIGHT + DASHBOARD_MARGIN);
    return contentHeight + slack;
  });

  readonly gridOptions: GridsterConfig = {
    gridType: GridType.VerticalFixed,
    fixedRowHeight: DASHBOARD_ROW_HEIGHT,
    // Free placement — compaction would yank a dragged widget back up every frame.
    compactType: CompactType.None,
    margin: DASHBOARD_MARGIN,
    outerMargin: false,
    minCols: DASHBOARD_GRID_COLS,
    maxCols: DASHBOARD_GRID_COLS,
    pushItems: true,
    mobileBreakpoint: DASHBOARD_MOBILE_BREAKPOINT,
    // Keep a dragged widget inside the grid, else it widens the page past the right edge.
    enableBoundaryControl: true,
    disableScrollHorizontal: true,
    displayGrid: DisplayGrid.None,
    draggable: { enabled: false, stop: () => this.compactAfterDrop() },
    resizable: { enabled: false, stop: () => this.compactAfterDrop() },
    itemChangeCallback: () => this.layoutRevision.update((revision) => revision + 1),
    itemResizeCallback: () => this.layoutRevision.update((revision) => revision + 1),
  };

  // Permission-driven visibility, kept out of the saved layout so a regained entitlement restores position.
  readonly visibleWidgetIds = computed<DashboardWidgetId[]>(() => {
    const subscriptionKpiVisible = this.loadingSubPayment() || this.subPayments().length > 0;
    return DASHBOARD_WIDGET_REGISTRY.map((widget) => widget.id).filter((id) => {
      switch (id) {
        case 'kpi-subscription':
          return subscriptionKpiVisible;
        case 'kpi-patient-billing':
          return this.billingEnabled();
        case 'patient-invoices':
          return this.hasBillingPermission();
        case 'upcoming-appointments':
          return this.hasApptPermission();
        default:
          return true;
      }
    });
  });

  // ── Birthdays & births ──
  readonly birthdays = signal<Birthday[]>([]);
  private readonly upcomingBirthsRaw = signal<UpcomingBirthRaw[]>([]);
  readonly upcomingBirths = computed<UpcomingBirth[]>(() => {
    this.lang(); // re-translate when the interface language changes
    return this.upcomingBirthsRaw().map((b) => ({
      ...b,
      label: this.upcomingBirthLabel(b.daysLeft),
    }));
  });

  // Own load state: upcoming births come from a separate call than getDashboard.
  readonly loadingUpcomingBirths = signal(true);

  // Phone just copied (drives the "Copied" tooltip); clears after a moment.
  readonly copiedPhone = signal<string | null>(null);

  copyPhone(phone: string, tip?: MatTooltip): void {
    navigator.clipboard.writeText(phone).then(() => {
      this.copiedPhone.set(phone);
      // Force the tooltip open so the "Copied" confirmation shows without hovering.
      tip?.show();
      setTimeout(() => {
        this.copiedPhone.set(null);
        tip?.hide();
      }, 2000);
    });
  }

  openBirthdayPatient(pid: number): void {
    this.patientsService.getPatient(pid, (s: unknown) => {
      if (s) {
        this.router.navigate(['/exams'], { state: { data: this.patientsService.currentPatient } });
      }
    });
  }

  // ── Patient invoices ──
  readonly patientInvoices = signal<Invoice[]>([]);
  readonly filteredInvoices = computed(() => {
    const period = this.billingPeriod();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const invoices = this.patientInvoices();
    if (period === 'today') {
      return invoices.filter((invoice) => {
        if (!invoice.rawDate) return false;
        const d = new Date(
          invoice.rawDate.getFullYear(),
          invoice.rawDate.getMonth(),
          invoice.rawDate.getDate()
        );
        return d.getTime() === today.getTime();
      });
    }
    if (period === 'week') {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - 6);
      return invoices.filter((invoice) => invoice.rawDate && invoice.rawDate >= weekStart);
    }
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return invoices.filter((invoice) => invoice.rawDate && invoice.rawDate >= monthStart);
  });
  readonly invoiceModalUrl = signal<SafeResourceUrl | null>(null);

  openInvoice(inv: Invoice): void {
    if (!inv.autoIndex) return;
    const url = `/api/dashboard/invoices/${inv.autoIndex}/pdf`;
    this.invoiceModalUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
  }

  closeInvoiceModal(): void {
    this.invoiceModalUrl.set(null);
  }

  // ── Subscription payments (for KPI row) ──
  readonly subPayments = signal<SubscriptionPaymentApi[]>([]);
  readonly loadingSubPayment = signal(true);
  readonly hasMorningAccess = signal<boolean | null>(null);

  // ── Data loading ──
  // Adopt the saved arrangement as baseline; null leaves the default in place.
  private applySavedLayout(saved: SavedLayoutItem[] | null): void {
    if (!saved?.length) {
      return;
    }
    this.committedLayout = saved;
    this.layout.set(mergeDashboardLayout(saved, this.visibleWidgetIds()));
  }

  private loadDashboard(): void {
    this.dashboardService.getDashboard().subscribe({
      next: (data) => {
        this.loading.set(false);
        this.skipOnLogin.set(data.skipOnLogin);
        this.birthdays.set(this.mapBirthdays(data.birthdays));
        // Apply saved layout before revealing the grid, so it never flashes the default.
        this.applySavedLayout(data.layout);
        this.layoutReady.set(true);
      },
      error: (err) => {
        console.error('Dashboard load failed', err);
        this.loading.set(false);
        // Reveal the grid on its default arrangement.
        this.layoutReady.set(true);
      },
    });

    this.dashboardService.getBillingStats('week').subscribe({
      next: (data) => {
        this.billingData.set(data);
        this.billingIsClinicWide.set(data.isClinicWide ?? false);
        this.loadingBilling.set(false);
      },
      error: () => this.loadingBilling.set(false),
    });

    this.dashboardService.getInvoices().subscribe({
      next: (data) => {
        this.patientInvoices.set(this.mapInvoices(data));
        this.loadingInvoices.set(false);
      },
      error: () => this.loadingInvoices.set(false),
    });

    this.dashboardService.getStorageStats('month').subscribe({
      next: (data) => {
        this.storageData.set(data);
        this.loadingStorage.set(false);
      },
      error: () => this.loadingStorage.set(false),
    });

    this.dashboardService.getUpcomingBirths().subscribe({
      next: (data) => {
        this.upcomingBirthsRaw.set(this.mapUpcomingBirthsRaw(data));
        this.loadingUpcomingBirths.set(false);
      },
      error: () => this.loadingUpcomingBirths.set(false),
    });

    this.dashboardService.getStorageSize().subscribe({
      next: (data) => {
        this.storageSizeLabel.set(data.formattedSize);
        this.storageBytes.set(data.bytes);
        this.loadingStorageSize.set(false);
      },
      error: () => {
        this.storageSizeLabel.set('N/A');
        this.loadingStorageSize.set(false);
      },
    });

    this.dashboardService.getSubscriptionPayment().subscribe({
      next: (data) => {
        if (data === null) {
          this.hasMorningAccess.set(false);
        } else {
          this.hasMorningAccess.set(true);
          this.subPayments.set(data);
        }
        this.loadingSubPayment.set(false);
      },
      error: () => this.loadingSubPayment.set(false),
    });
  }

  // ── Mapping helpers ──
  private mapBirthdays(data: BirthdayApi[]): Birthday[] {
    return data.map((b) => {
      const dt = new Date(b.Birth);
      return {
        pid: b.PID,
        name: `${b.First} ${b.Last}`,
        date: `${MONTH_ABBR[dt.getMonth()]} ${dt.getDate()}`,
        phone: b.Mobile?.trim() || b.Phone?.trim() || null,
        email: b.Email?.trim() || null,
      };
    });
  }

  private mapInvoices(data: InvoiceApi[]): Invoice[] {
    return data.map((inv) => {
      const parts = (inv.Recipient ?? '').trim().split(/\s+/);
      const initials =
        parts.length >= 2
          ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
          : (parts[0]?.[0] ?? '').toUpperCase();
      const dt = inv.DateCreated ? new Date(inv.DateCreated) : null;
      const date = dt ? `${MONTH_ABBR[dt.getMonth()]} ${dt.getDate()}` : '';
      return {
        initials,
        name: inv.Recipient ?? '',
        id: inv.DocumentNumber ? `#${inv.DocumentNumber}` : `INV-${inv.AutoIndex}`,
        amount: `₪${inv.Price}`,
        rawAmount: inv.Price,
        status: 'paid' as const,
        typeName: inv.TypeName ?? '',
        date,
        rawDate: dt ?? undefined,
        autoIndex: inv.AutoIndex,
        copyLink: inv.CopyLink,
      };
    });
  }

  private mapUpcomingBirthsRaw(data: UpcomingBirthApi[]): UpcomingBirthRaw[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return data.map((b) => {
      const edd = new Date(b.EDD);
      edd.setHours(0, 0, 0, 0);
      const daysLeft = Math.round((edd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return {
        name: `${b.First} ${b.Last}`,
        edd: `${MONTH_ABBR[edd.getMonth()]} ${edd.getDate()}`,
        daysLeft,
        pid: b.PID,
        phone: b.Mobile?.trim() || b.Phone?.trim() || null,
        email: b.Email?.trim() || null,
      };
    });
  }

  private upcomingBirthLabel(daysLeft: number): string {
    if (daysLeft === 0) return this.translate.instant('GUI.dashboard.labels.chips.due today');
    if (daysLeft === 1) return this.translate.instant('GUI.dashboard.labels.chips.due tomorrow');
    const days = this.translate.instant('GUI.dashboard.labels.days');
    if (daysLeft > 1)
      return `${this.translate.instant('GUI.dashboard.labels.in')} ${daysLeft} ${days}`;
    return `${this.translate.instant('GUI.dashboard.labels.overdue')} ${Math.abs(daysLeft)} ${days}`;
  }
}
